import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collectDiagnostics, downloadDiagnosticsAsFile, DiagnosticsReport } from '../utils/diagnosticsCollector';
import { diagnosticsApi } from '../services/api';
import DiagnosticAttachments, { DiagnosticAttachmentsValue } from '../components/DiagnosticAttachments';

type Phase = 'idle' | 'collecting' | 'preview' | 'sending' | 'sent' | 'fallback' | 'error';

export default function SendDiagnostics() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('idle');
  const [report, setReport] = useState<DiagnosticsReport | null>(null);
  const [refId, setRefId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Attachments live OUTSIDE the collected report so the rep can keep
  // editing them after pressing Gather. Merged into report.context at
  // submit time. (#1965)
  const [attachments, setAttachments] = useState<DiagnosticAttachmentsValue>({
    notes: '',
    screenshots: [],
  });
  // #2207 — opt-in: include the offline sync state (queued items, task sessions,
  // survey responses) so support can debug sync issues. Off by default.
  const [includeOfflineState, setIncludeOfflineState] = useState(false);

  const handleCollect = async () => {
    setPhase('collecting');
    setError(null);
    try {
      const r = await collectDiagnostics({ context: { type: 'quick' }, includeOfflineState });
      setReport(r);
      setPhase('preview');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPhase('error');
    }
  };

  const handleSend = async () => {
    if (!report) return;
    setPhase('sending');
    setError(null);
    try {
      // Merge user-entered notes + screenshots into the collected
      // report's context block. The server reads this from
      // payload.context.attachments. (#1965)
      const reportWithAttachments: DiagnosticsReport = {
        ...report,
        context: {
          ...report.context,
          attachments: {
            notes: attachments.notes.trim(),
            screenshots: attachments.screenshots,
          },
        },
      };
      const result = await diagnosticsApi.submit(reportWithAttachments);
      setRefId(result.report_id);
      setPhase('sent');
    } catch (err) {
      // POST failed (offline, server down, etc). Fall back to file download.
      console.warn('[SendDiagnostics] POST failed, falling back to file download', err);
      setError(err instanceof Error ? err.message : String(err));
      setPhase('fallback');
    }
  };

  const handleDownload = () => {
    if (!report) return;
    downloadDiagnosticsAsFile(report);
  };

  const summary = report
    ? [
        `App: ${report.app.commit.slice(0, 8)} (${report.app.branch})`,
        `Device: ${report.device.platform || 'unknown'}`,
        `PWA: ${report.pwa.displayMode}`,
        `GPS: permission ${report.geolocation.permission}${
          report.geolocation.lastFix ? `, accuracy ${report.geolocation.lastFix.accuracy.toFixed(0)}m` : ''
        }`,
        `Online: ${report.network.online}`,
        `Recent errors: ${report.recentErrors.length}`,
        `IndexedDB stores: ${report.storage.indexedDB.length}`,
      ]
    : [];

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="flex items-center gap-4 px-4 py-3">
          <button
            onClick={() => navigate(-1)}
            className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-slate-100"
            aria-label="Back"
          >
            <svg className="h-6 w-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-xl font-semibold text-slate-800">Send Device Report</h1>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* What's shared explainer */}
        <div className="rounded-xl bg-blue-50 border border-blue-200 p-4 text-sm text-blue-900">
          <p className="font-medium mb-1">What this sends to support</p>
          <p>
            App version, device type, PWA install state, GPS permission &amp; last fix, network status,
            storage usage, sync state, and the last few errors that happened in the app. <strong>No passwords,
            no auth tokens, no photo or survey contents.</strong>
          </p>
          <p className="mt-2 text-xs">
            Used to debug issues like "I'm being flagged as far from a store" or "the page won't scroll" —
            with a one-tap report, support can see exactly what your device is doing without needing screenshots.
          </p>
        </div>

        {/* Idle state */}
        {phase === 'idle' && (
          <div className="space-y-3">
            {/* #2207 — opt-in offline sync state for sync debugging. */}
            <label className="flex items-start gap-3 rounded-xl bg-white p-4 shadow-card cursor-pointer">
              <input
                type="checkbox"
                checked={includeOfflineState}
                onChange={(e) => setIncludeOfflineState(e.target.checked)}
                className="mt-0.5 h-5 w-5 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-slate-700">
                <span className="font-medium">Include offline sync state</span>
                <span className="block text-xs text-slate-500">
                  Adds your queued items, visit/task records, and survey answers (photo contents are
                  never included) so support can debug sync problems. Turn on only if asked.
                </span>
              </span>
            </label>
            <button
              onClick={handleCollect}
              className="w-full rounded-xl bg-primary-600 py-3 font-semibold text-white hover:bg-primary-700 active:scale-[0.98]"
            >
              Gather diagnostics
            </button>
          </div>
        )}

        {/* Collecting */}
        {phase === 'collecting' && (
          <div className="rounded-xl bg-white p-6 shadow-card text-center">
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
            <p className="mt-3 text-sm text-slate-600">Gathering device info, GPS state, storage usage…</p>
          </div>
        )}

        {/* Preview - ready to send */}
        {phase === 'preview' && report && (
          <>
            <div className="rounded-xl bg-white p-4 shadow-card">
              <h3 className="text-sm font-medium text-slate-700 mb-2">Summary</h3>
              <ul className="text-xs font-mono text-slate-600 space-y-1">
                {summary.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </div>
            <DiagnosticAttachments
              value={attachments}
              onChange={setAttachments}
              notesLabel="What happened?"
              notesHint="Tell support what you were doing, what you expected, and what went wrong. The more detail the faster they can help."
            />
            <button
              onClick={handleSend}
              className="w-full rounded-xl bg-primary-600 py-3 font-semibold text-white hover:bg-primary-700 active:scale-[0.98]"
            >
              Send to support
            </button>
            <button
              onClick={handleDownload}
              className="w-full rounded-xl border border-slate-300 bg-white py-3 font-medium text-slate-700 hover:bg-slate-50"
            >
              Download as file instead
            </button>
          </>
        )}

        {/* Sending */}
        {phase === 'sending' && (
          <div className="rounded-xl bg-white p-6 shadow-card text-center">
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
            <p className="mt-3 text-sm text-slate-600">Sending to support…</p>
          </div>
        )}

        {/* Sent successfully */}
        {phase === 'sent' && (
          <div className="rounded-xl bg-green-50 border border-green-200 p-4 text-center">
            <svg className="mx-auto h-12 w-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="mt-2 font-semibold text-green-800">Diagnostics sent</p>
            {refId !== null && (
              <p className="mt-1 text-xs text-green-700 font-mono">Reference: #{refId}</p>
            )}
            <p className="mt-2 text-sm text-green-700">
              When you contact support, mention this reference number and they'll be able to look up your device's state.
            </p>
            <button
              onClick={() => navigate(-1)}
              className="mt-4 rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white hover:bg-green-800"
            >
              Done
            </button>
          </div>
        )}

        {/* Fallback - couldn't reach server, offer download */}
        {phase === 'fallback' && report && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-4">
            <p className="font-semibold text-amber-900 mb-1">Couldn't reach support server</p>
            <p className="text-sm text-amber-800 mb-3">
              You may be offline. You can save the diagnostics to your device and email them to support manually.
            </p>
            <button
              onClick={handleDownload}
              className="w-full rounded-lg bg-amber-600 py-2 text-sm font-medium text-white hover:bg-amber-700"
            >
              Download diagnostics file
            </button>
            <button
              onClick={handleSend}
              className="mt-2 w-full rounded-lg border border-amber-300 bg-white py-2 text-sm font-medium text-amber-700 hover:bg-amber-50"
            >
              Try sending again
            </button>
          </div>
        )}

        {/* Error */}
        {phase === 'error' && (
          <div className="rounded-xl bg-red-50 border border-red-200 p-4">
            <p className="font-semibold text-red-900 mb-1">Something went wrong</p>
            <p className="text-sm text-red-800 font-mono">{error || 'unknown'}</p>
            <button
              onClick={handleCollect}
              className="mt-3 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
            >
              Try again
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
